<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { onMounted, ref, type Ref } from "vue";
import {
  createTier,
  deleteTier,
  listTiers,
  updateTier,
  type SaveTierInput,
  type ServiceTier,
  type TierStatus,
} from "../api/system-tiers.js";
import AppShell from "../components/AppShell.vue";
import { confirmDialog } from "../dialogs/app-dialog.js";

const loadingError: Ref<string | null> = ref(null);
const tiers: Ref<ServiceTier[]> = ref([]);
const selectedTierId: Ref<string | null> = ref(null);
const tierName: Ref<string> = ref("");
const tierDescription: Ref<string> = ref("");
const tierStatus: Ref<TierStatus> = ref("active");
const tierMaxProbes: Ref<number | string> = ref(10);
const tierMaxUsers: Ref<number | string> = ref(10);
const tierMaxNotificationContacts: Ref<number | string> = ref(50);
const tierProbeDelaySeconds: Ref<number | string> = ref(60);
const tierUploadIntervalSeconds: Ref<number | string> = ref(60);
const tierFullRetentionDays: Ref<number | string> = ref(90);
const tierAggregatedRetentionDays: Ref<number | string> = ref(365);
const tierMessage: Ref<string | null> = ref(null);
const tierSaveFailed: Ref<boolean> = ref(false);
const savingTier: Ref<boolean> = ref(false);
const tierNameError: Ref<string | null> = ref(null);
const tierMaxProbesError: Ref<string | null> = ref(null);
const tierMaxUsersError: Ref<string | null> = ref(null);
const tierMaxNotificationContactsError: Ref<string | null> = ref(null);
const tierProbeDelayError: Ref<string | null> = ref(null);
const tierUploadIntervalError: Ref<string | null> = ref(null);
const tierFullRetentionError: Ref<string | null> = ref(null);
const tierAggregatedRetentionError: Ref<string | null> = ref(null);

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "The tier request failed.";
}

function resetTierForm(): void {
  selectedTierId.value = null;
  tierName.value = "";
  tierDescription.value = "";
  tierStatus.value = "active";
  tierMaxProbes.value = 10;
  tierMaxUsers.value = 10;
  tierMaxNotificationContacts.value = 50;
  tierProbeDelaySeconds.value = 60;
  tierUploadIntervalSeconds.value = 60;
  tierFullRetentionDays.value = 90;
  tierAggregatedRetentionDays.value = 365;
  tierMessage.value = null;
  tierSaveFailed.value = false;
  clearTierValidation();
}

function applyTier(tier: ServiceTier): void {
  selectedTierId.value = tier.id;
  tierName.value = tier.name;
  tierDescription.value = tier.description ?? "";
  tierStatus.value = tier.status;
  tierMaxProbes.value = tier.maxProbesPerTenant;
  tierMaxUsers.value = tier.maxUsersPerTenant;
  tierMaxNotificationContacts.value = tier.maxNotificationContactsPerTenant;
  tierProbeDelaySeconds.value = tier.minProbeRequestDelayMs / 1000;
  tierUploadIntervalSeconds.value = tier.minProbeUploadIntervalMs / 1000;
  tierFullRetentionDays.value = tier.fullLogRetentionDays;
  tierAggregatedRetentionDays.value = tier.maxAggregatedLogRetentionDays;
  tierMessage.value = null;
  tierSaveFailed.value = false;
  clearTierValidation();
}

function tierInput(): SaveTierInput {
  return {
    name: tierName.value,
    description: tierDescription.value.trim() === "" ? null : tierDescription.value,
    status: tierStatus.value,
    maxProbesPerTenant: Number(tierMaxProbes.value),
    maxUsersPerTenant: Number(tierMaxUsers.value),
    maxNotificationContactsPerTenant: Number(tierMaxNotificationContacts.value),
    minProbeRequestDelayMs: Number(tierProbeDelaySeconds.value) * 1000,
    minProbeUploadIntervalMs: Number(tierUploadIntervalSeconds.value) * 1000,
    fullLogRetentionDays: Number(tierFullRetentionDays.value),
    maxAggregatedLogRetentionDays: Number(tierAggregatedRetentionDays.value),
  };
}

function clearTierValidation(): void {
  tierNameError.value = null;
  tierMaxProbesError.value = null;
  tierMaxUsersError.value = null;
  tierMaxNotificationContactsError.value = null;
  tierProbeDelayError.value = null;
  tierUploadIntervalError.value = null;
  tierFullRetentionError.value = null;
  tierAggregatedRetentionError.value = null;
}

function integerValue(value: string | number): number | null {
  const numericValue: number = Number(value);
  return Number.isInteger(numericValue) ? numericValue : null;
}

function validateTierName(): boolean {
  tierNameError.value = tierName.value.trim() === "" ? "Tier name is required." : null;
  return tierNameError.value === null;
}

function validateMinimumInteger(
  value: string | number,
  minimum: number,
  maximum: number,
  errorTarget: Ref<string | null>,
  label: string,
): boolean {
  const numericValue: number | null = integerValue(value);
  if (numericValue === null || numericValue < minimum) {
    errorTarget.value = `${label} must be at least ${minimum}.`;
    return false;
  }
  if (numericValue > maximum) {
    errorTarget.value = `${label} must not exceed ${maximum}.`;
    return false;
  }
  errorTarget.value = null;
  return true;
}

function validateAggregatedRetention(): boolean {
  const fullRetentionDays: number | null = integerValue(tierFullRetentionDays.value);
  const aggregatedRetentionDays: number | null = integerValue(tierAggregatedRetentionDays.value);
  if (
    aggregatedRetentionDays === null ||
    fullRetentionDays === null ||
    aggregatedRetentionDays < fullRetentionDays
  ) {
    tierAggregatedRetentionError.value =
      "Aggregated retention must be at least the full log retention.";
    return false;
  }
  if (aggregatedRetentionDays > 365000) {
    tierAggregatedRetentionError.value = "Aggregated retention must not exceed 365000.";
    return false;
  }
  tierAggregatedRetentionError.value = null;
  return true;
}

function validateTierForm(): boolean {
  const validName: boolean = validateTierName();
  const validMaxProbes: boolean = validateMinimumInteger(
    tierMaxProbes.value,
    1,
    1000000,
    tierMaxProbesError,
    "Max probes per tenant",
  );
  const validMaxUsers: boolean = validateMinimumInteger(
    tierMaxUsers.value,
    1,
    999,
    tierMaxUsersError,
    "Max users per tenant",
  );
  const validMaxNotificationContacts: boolean = validateMinimumInteger(
    tierMaxNotificationContacts.value,
    0,
    1000000,
    tierMaxNotificationContactsError,
    "Max notification contacts per tenant",
  );
  const validProbeDelay: boolean = validateMinimumInteger(
    tierProbeDelaySeconds.value,
    60,
    86400,
    tierProbeDelayError,
    "Minimum probe request delay",
  );
  const validUploadInterval: boolean = validateMinimumInteger(
    tierUploadIntervalSeconds.value,
    60,
    86400,
    tierUploadIntervalError,
    "Minimum probe upload interval",
  );
  const validFullRetention: boolean = validateMinimumInteger(
    tierFullRetentionDays.value,
    1,
    365000,
    tierFullRetentionError,
    "Full log retention",
  );
  const validAggregatedRetention: boolean = validateAggregatedRetention();
  return (
    validName &&
    validMaxProbes &&
    validMaxUsers &&
    validMaxNotificationContacts &&
    validProbeDelay &&
    validUploadInterval &&
    validFullRetention &&
    validAggregatedRetention
  );
}

function validateMaxProbes(): boolean {
  return validateMinimumInteger(
    tierMaxProbes.value,
    1,
    1000000,
    tierMaxProbesError,
    "Max probes per tenant",
  );
}

function validateMaxUsers(): boolean {
  return validateMinimumInteger(
    tierMaxUsers.value,
    1,
    999,
    tierMaxUsersError,
    "Max users per tenant",
  );
}

function validateMaxNotificationContacts(): boolean {
  return validateMinimumInteger(
    tierMaxNotificationContacts.value,
    0,
    1000000,
    tierMaxNotificationContactsError,
    "Max notification contacts per tenant",
  );
}

function validateProbeDelay(): boolean {
  const validProbeDelay: boolean = validateMinimumInteger(
    tierProbeDelaySeconds.value,
    60,
    86400,
    tierProbeDelayError,
    "Minimum probe request delay",
  );
  validateUploadInterval();
  return validProbeDelay;
}

function validateUploadInterval(): boolean {
  const validUploadInterval: boolean = validateMinimumInteger(
    tierUploadIntervalSeconds.value,
    60,
    86400,
    tierUploadIntervalError,
    "Minimum probe upload interval",
  );
  const probeDelaySeconds: number | null = integerValue(tierProbeDelaySeconds.value);
  const uploadIntervalSeconds: number | null = integerValue(tierUploadIntervalSeconds.value);
  if (
    validUploadInterval &&
    probeDelaySeconds !== null &&
    uploadIntervalSeconds !== null &&
    uploadIntervalSeconds < probeDelaySeconds
  ) {
    tierUploadIntervalError.value = "Minimum probe upload interval must be at least the probe request delay.";
    return false;
  }
  return validUploadInterval;
}

function validateFullRetention(): boolean {
  const validFullRetention: boolean = validateMinimumInteger(
    tierFullRetentionDays.value,
    1,
    365000,
    tierFullRetentionError,
    "Full log retention",
  );
  validateAggregatedRetention();
  return validFullRetention;
}

async function loadTiers(): Promise<void> {
  loadingError.value = null;
  try {
    tiers.value = [...await listTiers()];
    const selectedTier: ServiceTier | undefined =
      selectedTierId.value === null
        ? tiers.value[0]
        : tiers.value.find((tier: ServiceTier): boolean => tier.id === selectedTierId.value) ?? tiers.value[0];
    if (selectedTier === undefined) {
      resetTierForm();
    } else {
      applyTier(selectedTier);
    }
  } catch (error: unknown) {
    loadingError.value = errorText(error);
  }
}

async function saveTier(): Promise<void> {
  if (!validateTierForm()) {
    tierSaveFailed.value = true;
    tierMessage.value = "Fix the highlighted tier fields before saving.";
    return;
  }
  savingTier.value = true;
  tierMessage.value = null;
  tierSaveFailed.value = false;
  try {
    const tierId: string | null = selectedTierId.value;
    const creating: boolean = tierId === null;
    const savedTier: ServiceTier =
      tierId === null ? await createTier(tierInput()) : await updateTier(tierId, tierInput());
    await loadTiers();
    applyTier(savedTier);
    tierMessage.value = creating ? "Tier created." : "Tier saved.";
  } catch (error: unknown) {
    tierSaveFailed.value = true;
    tierMessage.value = errorText(error);
  } finally {
    savingTier.value = false;
  }
}

async function removeSelectedTier(): Promise<void> {
  const tierId: string | null = selectedTierId.value;
  if (tierId === null) {
    return;
  }
  const confirmed: boolean = await confirmDialog({
    title: "Delete tier?",
    message: "A tier can only be deleted when no tenant is assigned to it.",
    confirmLabel: "Delete tier",
    tone: "danger",
  });
  if (!confirmed) {
    return;
  }
  savingTier.value = true;
  tierMessage.value = null;
  tierSaveFailed.value = false;
  try {
    await deleteTier(tierId);
    selectedTierId.value = null;
    await loadTiers();
    tierMessage.value = "Tier deleted.";
  } catch (error: unknown) {
    tierSaveFailed.value = true;
    tierMessage.value = errorText(error);
  } finally {
    savingTier.value = false;
  }
}

onMounted(loadTiers);
</script>

<template>
  <AppShell>
    <header class="page-heading">
      <div>
        <p class="eyebrow">System administration</p>
        <h1>Tiers</h1>
        <p>Capacity and retention policies assigned to tenants.</p>
      </div>
      <button class="button button--primary page-heading__action" type="button" @click="resetTierForm">
        Add tier
      </button>
    </header>

    <p v-if="loadingError" class="form-error settings-load-error" role="alert">{{ loadingError }}</p>

    <section class="content-panel settings-card">
      <div class="settings-card__heading">
        <div><p class="eyebrow">Capacity</p><h2>Tenant tiers</h2></div>
        <span class="status-badge status-badge--active">{{ tiers.length }} configured</span>
      </div>
      <div class="smtp-profile-layout">
        <nav class="smtp-profile-list" aria-label="Service tiers">
          <button
            v-for="tier in tiers"
            :key="tier.id"
            type="button"
            :class="{ 'smtp-profile-list__item--selected': selectedTierId === tier.id }"
            class="smtp-profile-list__item"
            @click="applyTier(tier)"
          >
            <span>
              <strong>{{ tier.name }}</strong>
              <small>{{ tier.tenantCount }} tenant{{ tier.tenantCount === 1 ? "" : "s" }}</small>
            </span>
            <span class="status-dot" :class="tier.status === 'active' ? 'status-dot--active' : 'status-dot--disabled'"></span>
          </button>
          <p v-if="tiers.length === 0" class="prepared-empty-state__safety">No tiers exist yet.</p>
        </nav>

        <form class="settings-form settings-form--smtp tier-form" @submit.prevent="saveTier">
          <label class="settings-field--wide">
            <span>Tier name</span>
            <input v-model="tierName" maxlength="120" required placeholder="Standard" @blur="validateTierName" />
            <small v-if="tierNameError" class="field-error">{{ tierNameError }}</small>
          </label>
          <label class="settings-field--wide"><span>Description · optional</span><input v-model="tierDescription" maxlength="500" placeholder="Best effort household monitoring" /></label>
          <label><span>Status</span><select v-model="tierStatus"><option value="active">Active</option><option value="disabled">Disabled · legacy only</option></select></label>
          <label>
            <span>Max probes per tenant</span>
            <input v-model="tierMaxProbes" type="number" min="1" max="1000000" required inputmode="numeric" @blur="validateMaxProbes" />
            <small v-if="tierMaxProbesError" class="field-error">{{ tierMaxProbesError }}</small>
          </label>
          <label>
            <span>Max users per tenant</span>
            <input v-model="tierMaxUsers" type="number" min="1" max="999" required inputmode="numeric" @blur="validateMaxUsers" />
            <small v-if="tierMaxUsersError" class="field-error">{{ tierMaxUsersError }}</small>
          </label>
          <label>
            <span>Max notification contacts</span>
            <input v-model="tierMaxNotificationContacts" type="number" min="0" max="1000000" required inputmode="numeric" @blur="validateMaxNotificationContacts" />
            <small v-if="tierMaxNotificationContactsError" class="field-error">{{ tierMaxNotificationContactsError }}</small>
          </label>
          <label>
            <span>Min probe request delay · seconds</span>
            <input v-model="tierProbeDelaySeconds" type="number" min="60" max="86400" required inputmode="numeric" @blur="validateProbeDelay" />
            <small v-if="tierProbeDelayError" class="field-error">{{ tierProbeDelayError }}</small>
          </label>
          <label>
            <span>Min probe upload interval · seconds</span>
            <input v-model="tierUploadIntervalSeconds" type="number" min="60" max="86400" required inputmode="numeric" @blur="validateUploadInterval" />
            <small v-if="tierUploadIntervalError" class="field-error">{{ tierUploadIntervalError }}</small>
          </label>
          <label>
            <span>Full log retention · days</span>
            <input v-model="tierFullRetentionDays" type="number" min="1" max="365000" required inputmode="numeric" @blur="validateFullRetention" />
            <small v-if="tierFullRetentionError" class="field-error">{{ tierFullRetentionError }}</small>
          </label>
          <label>
            <span>Aggregated retention · days</span>
            <input v-model="tierAggregatedRetentionDays" type="number" min="1" max="365000" required inputmode="numeric" @blur="validateAggregatedRetention" />
            <small v-if="tierAggregatedRetentionError" class="field-error">{{ tierAggregatedRetentionError }}</small>
          </label>
          <div class="notice settings-field--wide">Hard limits: max users cannot exceed 999; notification contacts and probes cannot exceed 1000000; probe delay cannot go below 60 seconds; upload interval cannot go below 60 seconds. Disabled tiers remain valid for existing tenants but cannot be newly assigned.</div>
          <div class="settings-actions settings-field--wide">
            <p v-if="tierMessage" :class="tierSaveFailed ? 'form-error' : 'settings-message'" :role="tierSaveFailed ? 'alert' : 'status'">{{ tierMessage }}</p>
            <div>
              <button v-if="selectedTierId !== null" class="button button--danger" type="button" :disabled="savingTier" @click="removeSelectedTier">Delete</button>
              <button class="button button--primary" type="submit" :disabled="savingTier">{{ savingTier ? "Saving…" : selectedTierId === null ? "Create tier" : "Save tier" }}</button>
            </div>
          </div>
        </form>
      </div>
    </section>
  </AppShell>
</template>
